import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Store, Search, Plus, RefreshCw, ExternalLink, CalendarDays,
  Users, Flame, Loader2, Settings, Info, MoreHorizontal, ChevronRight,
  Archive, ArrowUpDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import {
  getGarageSales, getGarageSaleStats, syncGarageSales, autoSyncGarageSales,
  updateGarageSale, MANUAL_SOURCES,
} from "../../../api/garageSales";
import { getAgents } from "../../../api/agent";
import GarageSaleDrawer from "../components/GarageSaleDrawer";
import AddGarageSaleModal from "../components/AddGarageSaleModal";
import WatchAreasModal from "../components/WatchAreasModal";
import { hasAdminAccess } from "../../../utils/roleAccess";

const TEAL = "#55c5d0";

// The signal is the hero: instead of the whole card screaming red, a small dot
// tells the eye "call this one" at a glance (CRM-2026-00037).
const SIGNAL = {
  high: { color: "#d5453f", label: "High signal — looks like a mover" },
  medium: { color: "#e0902a", label: "Worth a look" },
  low: { color: "#c2c8ce", label: "Ordinary clear-out" },
};
const signalOf = (sale) => SIGNAL[sale?.intent] || SIGNAL.low;
const INTENT_RANK = { high: 0, medium: 1, low: 2 };

const STATUS_STYLES = {
  new: "bg-[#e8f7f9] text-[#2a8f99]",
  in_progress: "bg-[#eef1fd] text-[#4a5bd0]",
  contacted: "bg-[#e9f7ee] text-[#34b56a]",
  dismissed: "bg-[#f2f3f5] text-[#8b939c]",
};
const STATUS_SHORT = {
  new: "New", in_progress: "Working", contacted: "Assigned", dismissed: "Archived",
};

// Three lanes, not six (CRM-2026-00037). "To Work" is the default and covers
// everything not yet dealt with (new + working); the per-card status chip still
// shows New vs Working inside it. The four stored statuses map onto these:
//   To Work  -> new + in_progress   Assigned -> contacted   Archived -> dismissed
// CG (#12975272406) renamed the "Contacted" lane to "Assigned".
const TABS = [
  { key: "to_work", label: "To Work" },
  { key: "contacted", label: "Assigned" },
  { key: "archived", label: "Archived" },
];
const TAB_STATUS_PARAM = { contacted: "contacted", archived: "dismissed" }; // to_work sends none

// Sort control (CRM-2026-00037 revision): newest/oldest by when the sale came
// in, hottest/coldest by intent score. "" keeps the default work order.
const SORT_OPTIONS = [
  { key: "", label: "Best to work" },
  { key: "hottest", label: "Hottest first" },
  { key: "coldest", label: "Coldest first" },
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
];

// A plain date field (2026-08-15) as 15/08/2026, formatted off the string so no
// timezone shift can move it a day.
const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "Updated 6 minutes ago". Rounded and plain — the exact second is noise here.
const sinceLabel = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  if (hrs === 1) return "1 hour ago";
  if (hrs < 24) return `${hrs} hours ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
};

// How the page keeps itself current without anyone pressing a button.
const REFRESH_MS = 90 * 1000;      // quiet re-read of the list while the tab is open
const MIN_RELOAD_GAP_MS = 30 * 1000; // floor between reloads, so tab-switching is cheap
const AUTO_SYNC_ASK_GAP_MS = 5 * 60 * 1000; // how often this tab asks the server to sync
const QUEUED_SYNC_SETTLE_MS = 8 * 1000;     // a queued sync runs on a worker; let it land

const Chip = ({ className = "", children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-extrabold ${className}`}>
    {children}
  </span>
);

// A stat tile. When `onClick` is given it becomes a filter button (CG
// #12975272406: "make likely movers & upcoming clickable to show which
// properties fit this") — `active` rings it while its filter is on.
const StatTile = ({ icon: Icon, label, value, tone = "#1f2937", onClick, active = false }) => {
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      title={clickable ? `Show only ${label.toLowerCase()}` : undefined}
      className={`bg-white rounded-xl border px-4 py-3 flex items-center gap-3 text-left w-full transition-colors ${
        clickable ? "cursor-pointer hover:border-gray-300 hover:bg-gray-50" : "cursor-default"
      } ${active ? "border-2" : "border border-gray-200"}`}
      style={active ? { borderColor: tone } : undefined}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${tone}14` }}>
        <Icon size={17} style={{ color: tone }} />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-extrabold text-gray-900 leading-none">{value}</div>
        <div className="text-[11px] text-gray-500 truncate">{label}</div>
      </div>
    </button>
  );
};

const GarageSalesPage = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [tab, setTab] = useState("to_work");
  const [sort, setSort] = useState("");
  const [intent, setIntent] = useState("all");
  const [archivingId, setArchivingId] = useState(null);
  const [suburb, setSuburb] = useState("");
  const [matchedOnly, setMatchedOnly] = useState(false);
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [openSaleId, setOpenSaleId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showAreas, setShowAreas] = useState(false);
  const [showHowto, setShowHowto] = useState(false);
  const [showSources, setShowSources] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Filters that apply to BOTH the list and the top counts. The tab (status) is
  // deliberately NOT in here: the stat tiles read as stable totals for the whole
  // book, so switching lanes doesn't make the numbers jump around.
  const baseParams = useMemo(() => {
    const p = { limit: 200 };
    if (intent !== "all") p.intent = intent;
    if (suburb) p.suburb = suburb;
    if (matchedOnly) p.matched_only = 1;
    if (upcomingOnly) p.upcoming_only = 1;
    if (debouncedQuery) p.q = debouncedQuery;
    return p;
  }, [intent, suburb, matchedOnly, upcomingOnly, debouncedQuery]);

  // The list adds the tab's status. "To Work" sends none (the API then hides
  // dismissed for us) and we split new vs working client-side below.
  const listParams = useMemo(() => {
    const p = { ...baseParams };
    const s = TAB_STATUS_PARAM[tab];
    if (s) p.status = s;
    if (sort) p.sort = sort;
    return p;
  }, [baseParams, tab, sort]);

  // A quiet load is a background refresh: no spinner over the grid and no toast
  // if it fails, because the agent did not ask for it and there is nothing for
  // them to do about it.
  const lastLoadRef = useRef(0);
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    lastLoadRef.current = Date.now();
    try {
      const [listRes, statsRes] = await Promise.all([
        getGarageSales(listParams),
        getGarageSaleStats(baseParams),
      ]);
      setSales(listRes.data?.results || []);
      setStats(statsRes.data || null);
      setFeed(statsRes.data?.feed || null);
    } catch (e) {
      if (!quiet) toast.error(e?.response?.data?.detail || "Could not load garage sales.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [listParams, baseParams]);

  useEffect(() => { load(); }, [load]);

  // Archive a sale straight from its card (CRM-2026-00037 revision) — stores
  // it under the Archived lane (status 'dismissed') without opening the drawer.
  const archiveSale = useCallback(async (sale) => {
    setArchivingId(sale.id);
    try {
      await updateGarageSale(sale.id, { status: "dismissed" });
      toast.success("Archived");
      load({ quiet: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive this sale.");
    } finally {
      setArchivingId(null);
    }
  }, [load]);

  // Kept in a ref so the auto-sync and polling effect below runs once and still
  // reloads with the filters that are current, instead of tearing down and
  // re-arming its timers every time a filter changes.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  const askedAtRef = useRef(0);
  const timersRef = useRef([]);
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  // A sync is running on a worker somewhere. Show that, then pick up the rows it
  // wrote. The timer is what clears the indicator, so it can never stick on.
  const waitForSyncToLand = useCallback(() => {
    setSyncing(true);
    timersRef.current.push(setTimeout(() => {
      setSyncing(false);
      loadRef.current({ quiet: true });
    }, QUEUED_SYNC_SETTLE_MS));
  }, []);

  // Ask the server to pull the feed. The server decides whether it actually
  // runs — it syncs only when the feed has gone stale and only one sync runs at
  // a time — so this is safe to call on every open, from every agent's browser.
  const maybeAutoSync = useCallback(async () => {
    if (Date.now() - askedAtRef.current < AUTO_SYNC_ASK_GAP_MS) return;
    askedAtRef.current = Date.now();
    try {
      const res = await autoSyncGarageSales();
      if (res.data?.feed) setFeed(res.data.feed);
      if (!res.data?.started) {
        // Someone else's page already kicked it off; wait for the same rows.
        if (res.data?.reason === "in_progress") waitForSyncToLand();
        return;
      }
      if (res.data.queued) {
        waitForSyncToLand();
      } else {
        // Already finished inside the request (no worker, local development).
        loadRef.current({ quiet: true });
      }
    } catch {
      // The scheduled job is still the main ingest; a failed nudge is not the
      // agent's problem, so it stays out of their way.
    }
  }, [waitForSyncToLand]);

  useEffect(() => {
    maybeAutoSync();

    const refresh = () => {
      if (Date.now() - lastLoadRef.current < MIN_RELOAD_GAP_MS) return;
      loadRef.current({ quiet: true });
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      maybeAutoSync();
      refresh();
    };

    document.addEventListener("visibilitychange", onVisible);
    const poll = setInterval(() => {
      // Only while someone is actually looking — a forgotten tab should not sit
      // there polling all afternoon.
      if (document.visibilityState === "visible") refresh();
    }, REFRESH_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(poll);
    };
  }, [maybeAutoSync]);

  useEffect(() => {
    // The assign dropdown. A failure here is not worth a toast — the rest of the
    // page works fine without it.
    getAgents({ limit: 200 })
      .then((res) => setAgents(res.data?.results || res.data || []))
      .catch(() => {});
  }, []);

  // The page syncs itself; this is the admin override for "pull it again right
  // now", which is why it ignores the staleness window.
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncGarageSales();
      toast.success(res.data?.detail || "Feed synced.");
      if (res.data?.feed) setFeed(res.data.feed);
      // A queued sync runs on a worker, so the new rows land a moment later.
      if (res.data?.queued) {
        waitForSyncToLand();
      } else {
        setSyncing(false);
        load({ quiet: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the sync.");
      setSyncing(false);
    }
  };

  const today = todayISO();

  // "To Work" holds two stored statuses; keep contacted out of it so the lane
  // only shows leads that still need action. Other tabs are already server-filtered.
  const visibleSales = useMemo(() => {
    if (tab !== "to_work") return sales;
    return sales.filter((s) => s.status === "new" || s.status === "in_progress");
  }, [sales, tab]);

  // CG #13226404699: "Upcoming Garage Sales" on top, then a separate "Past
  // Garage Sales" list, each ordered hottest to normal (Looks like a mover,
  // Worth a look, grey). A sale with no date isn't past, so it stays upcoming.
  // An explicit Sort choice keeps the server's order inside each section.
  const { upcomingSales, pastSales } = useMemo(() => {
    const upcoming = [];
    const past = [];
    visibleSales.forEach((s) => (s.sale_date && s.sale_date < today ? past : upcoming).push(s));
    if (!sort) {
      const byHeat = (dateDir) => (a, b) =>
        (INTENT_RANK[a.intent] ?? 2) - (INTENT_RANK[b.intent] ?? 2)
        || (b.intent_score || 0) - (a.intent_score || 0)
        || dateDir * String(a.sale_date || "9999").localeCompare(String(b.sale_date || "9999"));
      upcoming.sort(byHeat(1)); // soonest first
      past.sort(byHeat(-1));    // most recent first
    }
    return { upcomingSales: upcoming, pastSales: past };
  }, [visibleSales, today, sort]);

  const knownOwners = stats?.with_matches || 0;

  const renderSale = (sale) => {
    const isPast = sale.sale_date && sale.sale_date < today;
    const sig = signalOf(sale);
    return (
      <button
        key={sale.id}
        onClick={() => setOpenSaleId(sale.id)}
        className="group text-left bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition px-3.5 py-2.5"
      >
        <div className="flex items-center gap-3">
          {/* Signal dot — the "call this one" cue. */}
          <span
            title={sig.label}
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: sig.color }}
          />

          <div className="flex-1 min-w-0">
            {/* Address is the lead; it's the hero line. */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-extrabold text-gray-900 truncate">
                {[sale.street, sale.suburb].filter(Boolean).join(", ") || "Address not published"}
              </span>
              {sale.intent === "high" && (
                <Chip className="bg-[#fdeceb] text-[#d5453f] shrink-0">
                  <Flame size={11} /> Looks like a mover
                </Chip>
              )}
              {sale.intent === "medium" && (
                <Chip className="bg-[#fbf1e3] text-[#b7791f] shrink-0">Worth a look</Chip>
              )}
              {sale.match_count > 0 && (
                <Chip className="bg-[#e9f7ee] text-[#34b56a] shrink-0">
                  <Users size={11} /> {sale.match_count}
                </Chip>
              )}
            </div>

            {/* Secondary: the ad wording + when. */}
            <div className="flex items-center gap-2 flex-wrap text-[12px] text-gray-500 mt-0.5">
              <span className="truncate max-w-[22rem]">{sale.title || "Garage sale"}</span>
              {sale.sale_date && (
                <span className={`inline-flex items-center gap-1 shrink-0 ${isPast ? "text-gray-400" : ""}`}>
                  <CalendarDays size={12} />
                  {auDate(sale.sale_date)}{sale.sale_time ? ` · ${sale.sale_time}` : ""}{isPast ? " · past" : ""}
                </span>
              )}
              {sale.assigned_agent_name && (
                <span className="font-bold text-gray-600 shrink-0">{sale.assigned_agent_name}</span>
              )}
            </div>

            {/* Why the signal fired — so the agent knows why to call. */}
            {Array.isArray(sale.intent_reasons) && sale.intent_reasons.length > 0 && (
              <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                <span className="font-bold text-gray-600">Why:</span>{" "}
                {sale.intent_reasons.slice(0, 3).join(" · ")}
              </div>
            )}
          </div>

          {/* Archive + status + a quiet "work it" affordance on the right. */}
          <div className="flex items-center gap-2 shrink-0">
            {sale.status !== "dismissed" && (
              <span
                role="button"
                tabIndex={0}
                title="Archive"
                onClick={(e) => { e.stopPropagation(); archiveSale(sale); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); archiveSale(sale); } }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                {archivingId === sale.id
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Archive size={14} />}
              </span>
            )}
            <Chip className={STATUS_STYLES[sale.status] || STATUS_STYLES.new}>
              {STATUS_SHORT[sale.status] || sale.status_display}
            </Chip>
            <span className="hidden md:inline-flex items-center gap-0.5 text-[12px] font-bold text-gray-300 group-hover:text-gray-500">
              Work lead <ChevronRight size={14} />
            </span>
          </div>
        </div>
      </button>
    );
  };

  const renderSection = (title, list, testId) => (
    <section data-testid={testId}>
      <h2 className="flex items-center gap-2 text-sm font-extrabold text-gray-800 mb-2 px-0.5">
        {title}
        <span className="text-[11px] font-bold text-gray-400">{list.length}</span>
      </h2>
      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 py-5 text-center text-[12px] text-gray-400">
          None right now
        </div>
      ) : (
        <div className="grid gap-2">{list.map(renderSale)}</div>
      )}
    </section>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: `${TEAL}1f` }}>
            <Store size={20} style={{ color: TEAL }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-extrabold text-gray-900">Garage Sales</h1>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setShowHowto((v) => !v); setShowSources(false); }}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-gray-600"
                >
                  <Info size={13} /> How this works
                </button>
                {showHowto && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowHowto(false)} />
                    <div className="absolute left-0 top-6 z-20 w-80 bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-[12px] text-gray-600">
                      Garage Sale Finder pulls in on its own, so this list is current every time you
                      open it. Gumtree blocks automated requests and the Facebook group is private,
                      so check those by eye and use <b>Add a sale</b> for anything worth calling.
                    </div>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs md:text-sm text-gray-500 max-w-2xl">
              Find homeowners showing signs they may be preparing to move.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* The page pulls the feed itself, so this reads as status, not a
              chore. Agents only ever need to know it is current. */}
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-gray-500">
            {syncing ? (
              <>
                <Loader2 size={14} className="animate-spin" style={{ color: TEAL }} />
                Checking for new sales
              </>
            ) : feed?.last_synced_at ? (
              <>
                <RefreshCw size={13} className="text-gray-400" />
                Updated {sinceLabel(feed.last_synced_at)}
              </>
            ) : null}
          </span>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-bold"
            style={{ background: TEAL }}
          >
            <Plus size={16} /> Add a sale
          </button>

          {/* Sources we cannot poll, tucked away until wanted. */}
          <div className="relative">
            <button
              onClick={() => { setShowSources((v) => !v); setShowHowto(false); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-bold text-gray-700"
              title="Sources we cannot pull automatically"
            >
              <MoreHorizontal size={16} /> More sources
            </button>
            {showSources && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSources(false)} />
                <div className="absolute right-0 top-11 z-20 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-1.5">
                  {MANUAL_SOURCES.map((s) => (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowSources(false)}
                      className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-[13px] font-bold text-gray-700 hover:bg-gray-50"
                    >
                      {s.label} <ExternalLink size={13} className="text-gray-400" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          {isAdmin && (
            <>
              <button
                onClick={handleSync}
                disabled={syncing}
                title="The feed already syncs on its own. This pulls it again right now."
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-bold text-gray-700 disabled:opacity-60"
              >
                {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Sync now
              </button>
              <button
                onClick={() => setShowAreas(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-bold text-gray-700"
              >
                <Settings size={16} /> Areas
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tiles — the two that matter, each a filter you can click into
          (CG #12975272406: "make likely movers & upcoming clickable to show
          which properties fit this"; the plain "Garage sales" total was
          removed). Clicking toggles the matching filter on/off. */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatTile
          icon={Flame} label="Likely movers" value={stats?.high_intent ?? "—"} tone="#d5453f"
          active={intent === "high"}
          onClick={() => setIntent((v) => (v === "high" ? "all" : "high"))}
        />
        <StatTile
          icon={CalendarDays} label="Upcoming" value={stats?.upcoming ?? "—"} tone={TEAL}
          active={upcomingOnly}
          onClick={() => setUpcomingOnly((v) => !v)}
        />
      </div>

      {/* One toolbar: lanes + search + filters together. */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="flex items-center gap-1 shrink-0">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-bold ${
                tab === t.key ? "text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
              style={tab === t.key ? { background: TEAL } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:block w-px h-6 bg-gray-200 mx-1" />

        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address or suburb"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm"
          />
        </div>

        <select
          value={suburb}
          onChange={(e) => setSuburb(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm max-w-[170px]"
        >
          <option value="">All suburbs</option>
          {(stats?.suburbs || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
        >
          <option value="all">Any signal</option>
          <option value="high">Looks like a mover</option>
          <option value="medium">Worth a look</option>
          <option value="low">Ordinary clear-out</option>
        </select>

        <div className="relative">
          <ArrowUpDown size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            title="Sort"
            className="pl-8 pr-3 py-2 rounded-lg border border-gray-300 text-sm"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        <label className="inline-flex items-center gap-1.5 text-[12px] font-bold text-gray-600 px-1">
          <input type="checkbox" checked={upcomingOnly} onChange={(e) => setUpcomingOnly(e.target.checked)} />
          Upcoming
        </label>

        {/* Only offered when there is actually something to filter to. */}
        {knownOwners > 0 && (
          <label className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#34b56a] px-1">
            <input type="checkbox" checked={matchedOnly} onChange={(e) => setMatchedOnly(e.target.checked)} />
            <Users size={13} /> Known owner
          </label>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 flex items-center justify-center text-gray-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : visibleSales.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <Store size={30} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-bold text-gray-700">No garage sales here yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
            {isAdmin && !feed?.active_areas
              ? "Add the suburbs to watch under Areas — the feed starts pulling them straight away."
              : "The feed pulls in on its own through the day. You can also add a sale you spotted yourself."}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {renderSection("Upcoming Garage Sales", upcomingSales, "gs-upcoming")}
          {!upcomingOnly && renderSection("Past Garage Sales", pastSales, "gs-past")}
        </div>
      )}

      {openSaleId && (
        <GarageSaleDrawer
          saleId={openSaleId}
          agents={agents}
          onClose={() => setOpenSaleId(null)}
          onChanged={load}
        />
      )}
      {showAdd && (
        <AddGarageSaleModal
          agents={agents}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {showAreas && (
        <WatchAreasModal onClose={() => setShowAreas(false)} />
      )}
    </div>
  );
};

export default GarageSalesPage;
